import React, { useState } from 'react';
import { Box, Typography, Chip, Stack, Paper } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { AppCheckBox } from 'shared/components/AppComponents';
import UpdateProgressDialog from './UpdateProgressDialog';
import { AISystemsService } from 'services/AISystemService';
import { Utils } from 'shared/helpers';
import { useParams } from 'react-router-dom';
import { GateWithStatus } from './ApprovalGatesSection';

interface ApprovalGateCardProps {
  gate: GateWithStatus;
  index: number;
  onCheckboxChange: (gateId: string, currentStatus: boolean) => void;
  onRefresh: () => Promise<void>;
}

const ApprovalGateCard: React.FC<ApprovalGateCardProps> = ({
  gate,
  index,
  onCheckboxChange,
  onRefresh,
}) => {
  const { aiSystemId } = useParams<{ aiSystemId: string }>();
  const [dialogOpen, setDialogOpen] = useState(false);

  const isOverdue = gate.dueDate ? new Date(gate.dueDate) < new Date() : false;

  const handleCalendarClick = (): void => {
    setDialogOpen(true);
  };

  const handleCloseDialog = (): void => setDialogOpen(false);

  const saveOrUpdateGate = (data: { dueDate: string; notes: string }): void => {
    if (!aiSystemId) return;

    const payload = {
      ai_inventory_id: aiSystemId,
      audit_gate_id: gate.id,
      is_completed: true,
      due_date: data.dueDate || null,
      notes: data.notes || null,
    };

    AISystemsService.createUpdateAIApprovalGate(payload)
      .then(() => {
        setDialogOpen(false);
        onRefresh();
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  return (
    <>
      <Paper variant="outlined" className="mb-3">
        <Box className="p-3">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
          >
            <Stack direction="row" alignItems="center">
              <AppCheckBox
                name={`gate-${gate.id}`}
                onChange={() => onCheckboxChange(gate.id, !gate.completed)}
                checked={gate.completed}
              />
              <Typography fontWeight={600} mr={3}>
                {index}. {gate.name}
              </Typography>
              {gate.completed && gate.completedDate && !isOverdue && (
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                  label={`Completed ${gate.completedDate}`}
                  size="small"
                  sx={{
                    backgroundColor: '#D1F27A',
                    color: '#06362B',
                    fontWeight: 600,
                    fontSize: '0.6rem',
                    '& .MuiChip-icon': {
                      color: '#06362B',
                    },
                  }}
                />
              )}
              {isOverdue && (
                <Chip
                  label="Overdue"
                  size="small"
                  sx={{
                    backgroundColor: '#dc2626',
                    color: '#fff',
                    fontWeight: 500,
                    fontSize: '0.6rem',
                  }}
                />
              )}
            </Stack>

            <Stack direction="row" alignItems="center" gap={3}>
              {gate.dueDate && (
                <Chip label={`Due: ${gate.dueDate}`} size="small" />
              )}
              <CalendarTodayIcon
                sx={{
                  fontSize: 14,
                  color: 'text.secondary',
                  cursor: 'pointer',
                }}
                onClick={handleCalendarClick}
              />
            </Stack>
          </Stack>

          <Box sx={{ mt: 1.5, ml: 4.5 }}>
            <Stack direction="column" flexWrap="wrap" gap={2} mb={0.5}>
              <Typography fontSize="0.8rem" color="text.secondary">
                Required:{' '}
                <Typography
                  component="span"
                  fontSize="0.8rem"
                  color="text.primary"
                >
                  {gate.required}
                </Typography>
              </Typography>
              <Typography fontSize="0.8rem" color="text.secondary">
                Reviewers:{' '}
                <Typography
                  component="span"
                  fontSize="0.8rem"
                  color="text.primary"
                >
                  {gate.reviewers}
                </Typography>
              </Typography>
            </Stack>

            {gate.notes && (
              <Stack direction="row" alignItems="center" gap={0.5} mt={0.5}>
                <InsertDriveFileOutlinedIcon
                  sx={{ fontSize: 14, color: 'text.secondary' }}
                />
                <Typography fontSize="0.75rem" color="text.secondary" mt={0.5}>
                  {gate.notes}
                </Typography>
              </Stack>
            )}
          </Box>
        </Box>
      </Paper>

      <UpdateProgressDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSubmit={saveOrUpdateGate}
        initialData={{
          dueDate: gate.dueDate || '',
          notes: gate.notes || '',
        }}
      />
    </>
  );
};

export default ApprovalGateCard;
